/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Button } from "@components/button";
import "./account.scss";
import { Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { FC, memo, useCallback, useState } from "react";
import { Avatar } from "@components/avatar";
import { EditImageDialog } from "@dialogs/edit-image-dialog";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { GET_PROFILE_DETAILS, UPDATE_PROFILE_PICTURE } from "@queries/index";
import { User, UserType } from "@archetypes/auth";
import { CropType, FileUploaded } from "@archetypes/generic";
import { useToastContext } from "@contexts/toast";
import { useIsMinimumTablet } from "@hooks/use-media-query";
import { Permission } from "@contexts/permissions.context";
import { PermissionTypes } from "@archetypes/gql";
import { UploadMediaType } from "@archetypes/app";

interface AccountAreaProps {
  basePath: string;
}

const AccountAreaComponent: FC<AccountAreaProps> = ({ basePath }) => {
  const [isEditImageDialogOpen, toggleEditImageDialog] = useState(false);

  const client = useApolloClient();
  const { showToast } = useToastContext();

  const { data, loading: profileDataLoading } = useQuery<{
    getProfileDetails: User;
  }>(GET_PROFILE_DETAILS, {
    onError: () => {
      showToast({
        title: "Error",
        content: `Error fetching user data`,
        variant: "danger",
      });
    },
  });

  const [updateProfile] = useMutation<{ updateProfile: { profilePicture: string } }>(
    UPDATE_PROFILE_PICTURE,
  );

  const toggleEditImageDialogHandler = useCallback(() => {
    toggleEditImageDialog((prev) => !prev);
  }, []);

  const onFileUploaded = useCallback(
    async (result: FileUploaded) => {
      const fileUrl = result.fileUrl;
      await updateProfile({
        variables: {
          input: {
            profilePicture: fileUrl,
          },
        },
      });
      toggleEditImageDialogHandler();
    },
    [toggleEditImageDialogHandler, updateProfile],
  );

  const navigate = useNavigate();

  const handleSignOut = useCallback(async () => {
    try {
      await Auth.signOut();
      client.clearStore();
      navigate("/auth/signin");
    } catch {
      showToast({
        title: "Error",
        content: `Error signing out`,
        variant: "danger",
      });
    }
  }, [client, navigate, showToast]);

  const [showPopOver, setTogglePopOver] = useState(false);
  const togglePopOver = useCallback(() => {
    setTogglePopOver((prev) => !prev);
  }, []);

  const isMobile = !useIsMinimumTablet();

  return (
    <>
      <div
        className={`${data?.getProfileDetails?.companyLogo ? "icons-wrapper" : ""}`}
        role="button"
        onClick={togglePopOver}
        tabIndex={0}
      >
        {data?.getProfileDetails?.companyLogo ? (
          <div>
            <Avatar
              title={data?.getProfileDetails?.companyName}
              imageUrl={data?.getProfileDetails?.companyLogo}
              loading={profileDataLoading}
              size="profile"
            />
          </div>
        ) : null}

        <div>
          <Avatar
            title={data?.getProfileDetails?.name}
            imageUrl={data?.getProfileDetails?.profilePicture}
            loading={profileDataLoading}
            size="profile"
          />
        </div>
      </div>
      {showPopOver && (
        <>
          <Button type="button" className="account-mask" onClick={togglePopOver} />
          <div className="account-popover">
            <div className="d-flex">
              <Avatar
                size="medium"
                title={data?.getProfileDetails?.name}
                imageUrl={data?.getProfileDetails?.profilePicture}
                onEditImage={toggleEditImageDialogHandler}
                loading={profileDataLoading}
              />
              <div className="account-info">
                <p className="name">
                  <span>{data?.getProfileDetails?.name}</span>
                </p>
                <p className="email">{data?.getProfileDetails?.email}</p>
              </div>
            </div>
            <ul className="account-links" onClick={togglePopOver}>
              <li>
                <Link to={`${basePath}/settings/profile`}>Profile</Link>
              </li>
              <Permission access={[PermissionTypes.COMPANY_READ]}>
                <li>
                  <Link to={`${basePath}/settings/organization`}>My Organization</Link>
                </li>
              </Permission>
              <li>
                <Link to={`${basePath}/settings/password`}>Password & Security</Link>
              </li>
              {data?.getProfileDetails?.userType !== UserType.ADMIN || isMobile ? (
                <li>
                  <Link to="/apps">My Apps</Link>
                </li>
              ) : (
                <li>
                  <Link to={basePath === "/apps" ? "/builder/apps" : "/apps"}>
                    {basePath === "/apps" ? "Switch To Builder Mode" : "My Apps"}
                  </Link>
                </li>
              )}
              <li>
                <p>App Version: {__APP_VERSION__}</p>
              </li>
            </ul>
            <Button className="logout" onClick={handleSignOut}>
              Log out
            </Button>
          </div>
        </>
      )}
      <EditImageDialog
        cropType={CropType.CIRCLE}
        fileType={UploadMediaType.Logo}
        title="Choose your profile picture"
        isOpen={isEditImageDialogOpen}
        onClose={toggleEditImageDialogHandler}
        onFileUploaded={onFileUploaded}
        filePath={data?.getProfileDetails?.email}
        fileName={data?.getProfileDetails?.name}
      />
    </>
  );
};

export const AccountArea = memo(AccountAreaComponent);
